import { useCallback } from 'react';
import { useLang } from 'hooks/lang';
import { TFunction } from 'i18next';

import { ExtraInformation } from 'entities';

import { LabeledTextarea, Column, LabeledSelect, Row } from 'components/Blocks';
import { TextToggle } from 'components/Inputs';

import { CreateProjectDispatchAction } from '../types';
import { BadgeDollarIcon, PencilIcon, UserSpeakIcon, UserSquareIcon } from 'components/Icons';

interface ExtraInformationProps {
  extraInformation: ExtraInformation;
  dispatch: CreateProjectDispatchAction;
}

const repriceOptions = [
  { value: 'NO', getLabel: (t: TFunction) => t('no') },
  { value: 'IR/LOI', getLabel: (t: TFunction) => t('yes-ir-loi') },
  { value: 'LOI_ONLY', getLabel: (t: TFunction) => t('yes-loi-only') },
  { value: 'END_OF_THE_FIELD', getLabel: (t: TFunction) => t('yes-end-of-the-field') },
];

export function ExtraInformationForm({ extraInformation, dispatch }: ExtraInformationProps) {
  const { t } = useLang();

  const handleInputChange = useCallback(
    ({ target }: { target: { name: string; value: string } }) => {
      const { name, value } = target;

      dispatch({
        observations: (old) => ({
          ...old.observations,
          [name]: value,
        }),
      });
    },
    [dispatch],
  );

  const setReprice = (repriceDetails: string): void => {
    dispatch({
      observations: (old) => ({
        ...old.observations,
        repriceDetails,
      }),
    });
  };

  const toggleRecruitment = (): void => {
    dispatch({
      observations: (old) => ({
        ...old.observations,
        recruitment: !old.observations.recruitment,
        recruitmentDetails: '',
      }),
    });
  };

  const setRecruitment = (recruitmentDetails: string): void => {
    dispatch({
      observations: (old) => ({
        ...old.observations,
        recruitmentDetails,
      }),
    });
  };

  return (
    <Column gap="6">
      <h6>{t('extra-information')}</h6>

      <Row className="phone:flex-col" alignItems="stretch">
        <LabeledTextarea
          id="bidsNotes"
          name="bidsNotes"
          value={extraInformation.bidsNotes}
          label={t('bids-notes') as string}
          className="min-h-24"
          onChange={handleInputChange}
          labelIcon={PencilIcon}
          fullWidth
        />

        <LabeledTextarea
          id="clientNotes"
          name="clientNotes"
          value={extraInformation.clientNotes}
          label={t('client-notes') as string}
          className="min-h-24"
          onChange={handleInputChange}
          labelIcon={UserSquareIcon}
          fullWidth
        />
      </Row>

      <TextToggle
        isActive={!!extraInformation.recruitment}
        toggle={toggleRecruitment}
        text={extraInformation.recruitmentDetails}
        setText={setRecruitment}
        label={t('recruitment-details')}
        labelIcon={UserSpeakIcon}
        description={t('explain-what-is-allowed-or-not-when-recruiting') || ''}
      />

      <LabeledSelect
        value={extraInformation.repriceDetails}
        onChangeDirect={setReprice}
        label={t('especial-reprice-condition') || ''}
        isSearchable={false}
        labelIcon={BadgeDollarIcon}
        options={repriceOptions.map(({ value, getLabel }) => ({
          value,
          label: getLabel(t),
        }))}
      />
    </Column>
  );
}
